import type {
  AppState,
  CandidateLens,
  OpticalLayer,
  SimulationPreset,
  SimulationSettings,
} from './types'

export const makeId = (prefix: string): string =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

export const simulationPresets: Record<SimulationPreset, SimulationSettings> = {
  preview: {
    preset: 'preview',
    sourceSamples: 5,
    apertureSamples: 13,
    lenslets: 9,
    viewerBins: 240,
    viewerSpanMm: 520,
  },
  balanced: {
    preset: 'balanced',
    sourceSamples: 9,
    apertureSamples: 27,
    lenslets: 17,
    viewerBins: 420,
    viewerSpanMm: 560,
  },
  high: {
    preset: 'high',
    sourceSamples: 15,
    apertureSamples: 51,
    lenslets: 29,
    viewerBins: 720,
    viewerSpanMm: 620,
  },
}

const exampleLayers: OpticalLayer[] = [
  {
    id: 'layer-substrate',
    name: 'LCD front substrate (example)',
    thicknessMm: 0.5,
    refractiveIndex: 1.52,
    enabled: true,
    thicknessToleranceMm: 0.02,
    indexTolerance: 0.005,
    distribution: 'normal',
    notes: 'Illustrative value. Replace with panel construction data.',
  },
  {
    id: 'layer-polarizer',
    name: 'Polarizer (example)',
    thicknessMm: 0.2,
    refractiveIndex: 1.48,
    enabled: true,
    thicknessToleranceMm: 0.01,
    indexTolerance: 0.005,
    distribution: 'normal',
    notes: 'Modeled as a homogeneous isotropic layer in v1.',
  },
  {
    id: 'layer-adhesive',
    name: 'Optical adhesive (example)',
    thicknessMm: 0.1,
    refractiveIndex: 1.47,
    enabled: true,
    thicknessToleranceMm: 0.015,
    indexTolerance: 0.01,
    distribution: 'normal',
    notes: 'Illustrative bond-line thickness.',
  },
  {
    id: 'layer-cover',
    name: 'Cover glass (example)',
    thicknessMm: 0.45,
    refractiveIndex: 1.52,
    enabled: true,
    thicknessToleranceMm: 0.02,
    indexTolerance: 0.005,
    distribution: 'normal',
    notes: 'Illustrative value. Glass thickness alone is not sufficient.',
  },
]

const exampleCandidate: CandidateLens = {
  id: 'candidate-example',
  name: 'Synthetic target-matched sheet',
  manufacturer: 'Worked example, not a vendor product',
  mode: 'thin',
  pitchMm: 0.7160949285733001,
  lpi: 25.4 / 0.7160949285733001,
  focalLengthMm: 0.8270208334966962,
  focalReference: 'effective-at-plane',
  radiusMm: null,
  refractiveIndex: null,
  centerThicknessMm: null,
  fillFactor: 0.94,
  pitchToleranceMm: 0.001,
  focalToleranceMm: 0.02,
  radiusToleranceMm: null,
  thicknessToleranceMm: null,
  lateralOffsetMm: 0,
  notes: 'Synthetic v1 screening input matched to the loaded example geometry. Replace every value with documented vendor or measured data.',
}

export const defaultState: AppState = {
  lcd: {
    activeWidthMm: 344.2,
    activeHeightMm: 193.6,
    resolutionX: 3840,
    resolutionY: 2160,
    sourceMode: 'pixel-columns',
    horizontalSubpixels: 3,
    customSourcePitchMm: null,
    apertureMode: 'fill-factor',
    sourceApertureMm: null,
    sourceFillFactor: 0.86,
    blackMatrixWidthMm: null,
    emissionModel: 'lambertian',
    gaussianHalfAngleDeg: 35,
    rgb: {
      red: { wavelengthNm: 630, refractiveIndex: 1.485 },
      green: { wavelengthNm: 530, refractiveIndex: 1.49 },
      blue: { wavelengthNm: 460, refractiveIndex: 1.498 },
    },
  },
  layers: exampleLayers,
  view: {
    views: 8,
    targetDistanceMm: 600,
    distanceMinMm: 520,
    distanceMaxMm: 680,
    targetSpacingMm: 65,
    eyeSeparationMm: 65,
    pupilDiameterMm: 4,
    headBoxWidthMm: 520,
    centerMode: 'even',
    customCentersMm: [],
    criteria: {
      maxCrosstalk: 0.1,
      maxZoneCenterErrorMm: 6,
      minRelativeThroughput: 0.18,
      maxAccumulatedDriftSourceCells: 0.5,
      marginalMultiplier: 1.25,
      enabled: {
        crosstalk: true,
        zoneCenter: true,
        throughput: true,
        phaseDrift: true,
        viewingRange: true,
      },
    },
  },
  lens: {
    mode: 'thin',
    sourcePhaseMm: 0,
    thin: {
      pitchMm: null,
      lpi: null,
      focalLengthMm: null,
      focalReference: 'effective-at-plane',
      fillFactor: 0.94,
      lateralOffsetMm: 0,
      pitchToleranceMm: 0.001,
      focalToleranceMm: 0.03,
      offsetToleranceMm: 0.01,
      useDesignTarget: true,
    },
    thick: {
      pitchMm: null,
      radiusMm: null,
      refractiveIndex: null,
      centerThicknessMm: null,
      externalIndex: 1,
      fillFactor: 0.94,
      lateralOffsetMm: 0,
      pitchToleranceMm: 0.001,
      radiusToleranceMm: 0.02,
      indexTolerance: 0.005,
      thicknessToleranceMm: 0.02,
      offsetToleranceMm: 0.01,
    },
  },
  candidates: [exampleCandidate],
  simulationSettings: simulationPresets.balanced,
  toleranceSettings: {
    trials: 500,
    seed: 127031,
    mode: 'analytical-paraxial',
    defaultDistribution: 'normal',
    targetDistanceToleranceMm: 10,
    sourceApertureToleranceMm: 0.002,
    pupilPositionToleranceMm: 1,
    includeCriteria: {
      crosstalk: true,
      zoneCenter: true,
      throughput: true,
      phaseDrift: true,
      viewingRange: true,
    },
  },
  exampleLoaded: true,
}

export const emptyState = (): AppState => ({
  ...structuredClone(defaultState),
  lcd: {
    ...structuredClone(defaultState.lcd),
    activeWidthMm: null,
    activeHeightMm: null,
    resolutionX: null,
    resolutionY: null,
    customSourcePitchMm: null,
    sourceApertureMm: null,
    sourceFillFactor: null,
    blackMatrixWidthMm: null,
  },
  layers: [],
  view: {
    ...structuredClone(defaultState.view),
    targetDistanceMm: null,
    distanceMinMm: null,
    distanceMaxMm: null,
    targetSpacingMm: null,
    eyeSeparationMm: null,
    pupilDiameterMm: null,
    headBoxWidthMm: null,
    customCentersMm: [],
  },
  lens: {
    ...structuredClone(defaultState.lens),
    thin: {
      ...structuredClone(defaultState.lens.thin),
      pitchMm: null,
      lpi: null,
      focalLengthMm: null,
      useDesignTarget: false,
    },
    thick: {
      ...structuredClone(defaultState.lens.thick),
      pitchMm: null,
      radiusMm: null,
      refractiveIndex: null,
      centerThicknessMm: null,
    },
  },
  candidates: [],
  exampleLoaded: false,
})
